import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { cloneDeep } from 'lodash'
import scriptjs from 'scriptjs'

import CkEditorTextbox from '@scoir/components-core/dist/components/CkEditorTextbox'
import CharactersRemaining from '@scoir/components-core/dist/components/CharactersRemaining'

import { TableCell, TableRow, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'

import { ErrorBoundary } from '@scoir/error-reporter'
import SectionTable from './components/SectionTable'

import sanitize from 'common/sanitize-html-helper'
import { getCharCount } from './textUtilities'

const useStyles = makeStyles((theme) => ({
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  exceedsError: {
    color: theme.palette.error.main,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loadingBox: {
    '& .MuiSkeleton-text': {
      transform: 'unset',
    },
  },
  thirdLevelHeading: {
    color: theme.palette.text.secondary,
    paddingBottom: 30,
  },
}))

const toolbar = [
  {
    name: 'basicstyles',
    items: [],
  },
  {
    name: 'insert',
    items: ['Link', 'Unlink'],
  },
]

const maxNoteLength = 2400
const essayText =
  'Please list any essay prompts applicants will be presented with on your supplement. If certain essays are only required for applicants meeting specific criteria, please note that as well.'

const SupplementalEssay = ({ isEditing, applicationConfig, setApplicationConfig, loading }) => {
  const classes = useStyles()
  const [loadedCkEditor, setLoadedCkEditor] = useState(false)
  useEffect(() => {
    scriptjs(CkEditorTextbox.config.CDN, () => {
      setLoadedCkEditor(true)
    })
  }, [])

  const onChange = (sanitizedVal) => {
    const updatedState = cloneDeep(applicationConfig)
    const isError = validateInput(sanitizedVal)

    const essayConfig = updatedState.supplementEssayInfo ? updatedState.supplementEssayInfo : {}
    essayConfig.body = sanitizedVal
    updatedState.supplementEssayInfo = { ...essayConfig }

    setApplicationConfig(updatedState, isError)
  }

  const validateInput = (val) => {
    const count = getCharCount(val)
    return count > maxNoteLength
  }

  const initalVal =
    applicationConfig.supplementEssayInfo && applicationConfig.supplementEssayInfo.body
      ? applicationConfig.supplementEssayInfo.body
      : ''

  const isError = validateInput(sanitize(initalVal))

  return (
    <SectionTable
      sectionName="Supplement Essay Prompts"
      onlyShowLabel={true}
    >
      <TableRow>
        {loading ? (
          <TableCell>
            <Skeleton
              data-testid="loader-test-id"
              className={classes.loadingBox}
              height={337}
            />
          </TableCell>
        ) : isEditing ? (
          <TableCell>
            <Typography
              variant="body2"
              className={classes.thirdLevelHeading}
            >
              {essayText}
            </Typography>
            {loadedCkEditor ? (
              <React.Fragment>
                <ErrorBoundary>
                  <CkEditorTextbox
                    initialContent={initalVal}
                    configuration="LINKS"
                    ckEditor={window.CKEDITOR}
                    additionalOptions={{ startupFocus: false, toolbar, versionCheck: false }}
                    onUpdate={(value) => onChange(sanitize(value))}
                  />
                </ErrorBoundary>

                <CharactersRemaining
                  textLength={getCharCount(initalVal)}
                  maxChars={maxNoteLength}
                />

                {isError && <Typography className={classes.exceedsError}>Exceeds character count.</Typography>}
              </React.Fragment>
            ) : (
              <Skeleton
                data-testid="loader-test-id"
                className={classes.loadingBox}
                height={337}
              />
            )}
          </TableCell>
        ) : (
          <TableCell>
            <Typography
              variant="body2"
              className={classes.thirdLevelHeading}
            >
              {essayText}
            </Typography>
            {initalVal ? (
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: sanitize(initalVal),
                }}
              ></Typography>
            ) : (
              <Typography
                variant="body2"
                className={classes.secondaryText}
              >
                None
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
    </SectionTable>
  )
}

SupplementalEssay.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default SupplementalEssay
